import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { useAppDispatch } from 'src/redux/storeHooks'
import { usePlatformCategoryTree } from 'src/app/pages/create-product/hooks/usePlatformCategoryTree'
import { buildFieldTree, FieldTreeNode } from 'src/utils/buildFieldTree'
import { getSelectedCategoryTreeAsync } from 'src/redux/actions/templateAndPricingActions'
import {
  updateCategoryAsync,
  createCategoryAsync,
  deleteCategoryAsync,
} from 'src/redux/actions/omsProductActions'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faChevronDown,
  faChevronRight,
  faMagnifyingGlass,
  faPlus,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { getApiErrorMessage, isApiPayloadSuccess } from 'src/marketConnect/helpers'
import './CategoryManagePage.scss'

type ResolvedCategory = {
  id: number
  api_name: string
  parentId: number | null
  pathLabels: string[]
  raw: any
}

const CategoryManagePage = () => {
  const { platformId } = useParams<{ platformId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { results, loading, search, hasMore, loadMore } = usePlatformCategoryTree(Number(platformId))

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20) {
        if (hasMore && !loading) {
          loadMore()
        }
      }
    },
    [hasMore, loading, loadMore]
  )

  const [treeData, setTreeData] = useState<FieldTreeNode[]>([])
  const [inputTerm, setInputTerm] = useState('')

  const [selectedNode, setSelectedNode] = useState<FieldTreeNode | null>(null)
  const [selectedPathLabels, setSelectedPathLabels] = useState<string[]>([])
  const [resolvedCategory, setResolvedCategory] = useState<ResolvedCategory | null>(null)
  const [resolvingCategory, setResolvingCategory] = useState(false)
  const [actionMode, setActionMode] = useState<'none' | 'addRoot'>('none')
  const [actionInputName, setActionInputName] = useState('')
  const [subcategoryInputName, setSubcategoryInputName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)
  const resolveSeqRef = useRef(0)

  useEffect(() => {
    setTreeData(buildFieldTree(results))
  }, [results])

  const updateNode = (
    nodes: FieldTreeNode[],
    id: number,
    updater: (n: FieldTreeNode) => FieldTreeNode
  ): FieldTreeNode[] =>
    nodes.map((n) =>
      n.id === id
        ? updater(n)
        : { ...n, children: n.children ? updateNode(n.children, id, updater) : n.children }
    )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value
    setInputTerm(term)
    search(term)
  }

  const getFirstLeafItem = (node: FieldTreeNode): any | null => {
    if (node.item?.id) return node.item
    for (const child of node.children || []) {
      const item = getFirstLeafItem(child)
      if (item) return item
    }
    return null
  }

  const loadCategoryPath = async (leafId: number) => {
    const path: any[] = await dispatch(
      getSelectedCategoryTreeAsync({ category_data_id: leafId })
    ).unwrap()

    return Array.isArray(path) ? path : []
  }

  const toResolvedCategory = (
    item: any,
    pathLabels: string[]
  ): ResolvedCategory | null => {
    if (!item?.id) return null

    return {
      id: Number(item.id),
      api_name: item.api_name,
      parentId: item.parent_data ?? null,
      pathLabels,
      raw: item,
    }
  }

  const resolveCategoryNode = async (
    node: FieldTreeNode,
    pathLabels: string[]
  ): Promise<ResolvedCategory | null> => {
    const sourceLeaf = node.item?.id ? node.item : getFirstLeafItem(node)
    if (!sourceLeaf?.id || pathLabels.length === 0) return null

    const path = await loadCategoryPath(Number(sourceLeaf.id))

    if (!Array.isArray(path) || path.length === 0) return null

    const target =
      node.item?.id
        ? path.find((item) => Number(item.id) === Number(node.item?.id))
        : path[pathLabels.length - 1]

    const expectedLabel = pathLabels[pathLabels.length - 1]?.trim().toLowerCase()
    const actualLabel = target?.api_name?.trim().toLowerCase()

    if (!target?.id || expectedLabel !== actualLabel) {
      return null
    }

    return toResolvedCategory(target, pathLabels)
  }

  const clearSelection = () => {
    resolveSeqRef.current += 1
    setSelectedNode(null)
    setSelectedPathLabels([])
    setResolvedCategory(null)
    setResolvingCategory(false)
    setActionMode('none')
  }

  const handleSelectNode = async (node: FieldTreeNode, pathLabels: string[]) => {
    const seq = ++resolveSeqRef.current
    setSelectedNode(node)
    setSelectedPathLabels(pathLabels)
    setResolvedCategory(null)
    setActionMode('none')
    setActionInputName(node.api_name)
    setSubcategoryInputName('')
    setResolvingCategory(true)

    try {
      const resolved = await resolveCategoryNode(node, pathLabels)
      if (seq !== resolveSeqRef.current) return
      setResolvedCategory(resolved)
      setActionInputName(resolved?.api_name || node.api_name)
      if (!resolved) {
        toast.error('Unable to resolve this category for editing. Please select a more specific result.')
      }
    } catch (error: any) {
      if (seq !== resolveSeqRef.current) return
      setResolvedCategory(null)
      toast.error(getApiErrorMessage(error))
    } finally {
      if (seq === resolveSeqRef.current) {
        setResolvingCategory(false)
      }
    }
  }

  const handleSelectPath = (pathLabels: string[]) => {
    let nodes = treeData
    let node: FieldTreeNode | undefined

    for (const label of pathLabels) {
      node = nodes.find((item) => item.api_name === label)
      if (!node) return
      nodes = node.children || []
    }

    if (node) handleSelectNode(node, pathLabels)
  }

  const handleAddChildFromTree = async (node: FieldTreeNode, pathLabels: string[]) => {
    await handleSelectNode(node, pathLabels)
  }

  const renderHighlightedText = (text: string, term?: string) => {
    const trimmedTerm = term?.trim()
    if (!trimmedTerm) return text

    const escapedTerm = trimmedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escapedTerm})`, 'ig'))
    const lowerTerm = trimmedTerm.toLowerCase()

    return parts.map((part, index) =>
      part.toLowerCase() === lowerTerm ? (
        <mark key={`${part}-${index}`} className='px-1 py-0 rounded bg-warning bg-opacity-25'>
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    )
  }

  const renderTreeNodes = (
    items: FieldTreeNode[],
    level: number,
    parentPath: string[] = []
  ): JSX.Element[] =>
    items.map((node) => {
      const pathLabels = [...parentPath, node.api_name]
      const isSelected =
        selectedNode === node ||
        (selectedNode?.id === node.id && selectedPathLabels.join(' / ') === pathLabels.join(' / '))
      return (
        <div key={`${node.id}-${pathLabels.join('/')}`} className='category-manage-tree-node'>
          <div
            className={`category-manage-tree-row${isSelected ? ' is-selected' : ''}`}
            style={{ paddingLeft: `${level * 22}px` }}
          >
            <button
              type='button'
              className='category-manage-tree-toggle'
              aria-label={node.children?.length ? `${node.isExpanded ? 'Collapse' : 'Expand'} ${node.api_name}` : `${node.api_name} has no visible subcategories`}
              disabled={!node.children?.length}
              onClick={() =>
                setTreeData((prev) =>
                  updateNode(prev, node.id, (item) => ({ ...item, isExpanded: !item.isExpanded }))
                )
              }
            >
              <FontAwesomeIcon icon={node.isExpanded ? faChevronDown : faChevronRight} />
            </button>
            <button
              type='button'
              className='category-manage-tree-label'
              onClick={() => handleSelectNode(node, pathLabels)}
            >
              <i className='bi bi-folder category-manage-folder-icon' aria-hidden='true' />
              <span className='category-manage-tree-name'>
                {renderHighlightedText(node.api_name, inputTerm)}
              </span>
              {node.children?.length ? (
                <span className='category-manage-count'>{node.children.length}</span>
              ) : null}
            </button>
            <button
              type='button'
              className='category-manage-row-add'
              title={`Add subcategory under ${node.api_name}`}
              aria-label={`Add subcategory under ${node.api_name}`}
              disabled={submitting}
              onClick={() => handleAddChildFromTree(node, pathLabels)}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          {node.isExpanded && node.children?.length ? (
            <div>{renderTreeNodes(node.children, level + 1, pathLabels)}</div>
          ) : null}
        </div>
      )
    })

  const handleActionSubmit = async (
    mode: 'editName' | 'addChild' | 'addRoot',
    submittedName: string
  ) => {
    if ((mode !== 'addRoot' && !selectedNode) || !submittedName.trim()) return
    if (
      mode === 'editName' &&
      (!resolvedCategory || submittedName.trim() === resolvedCategory.api_name?.trim())
    ) return
    setSubmitting(true)
    try {
      let response: any
      if (mode === 'editName') {
        if (!resolvedCategory) return
        response = await dispatch(
          updateCategoryAsync({
            id: resolvedCategory.id,
            data: {
              api_name: submittedName.trim(),
              category_parent_id: resolvedCategory.parentId,
            },
          })
        ).unwrap()
      } else if (mode === 'addChild') {
        if (!resolvedCategory) return
        response = await dispatch(
          createCategoryAsync({
            api_name: submittedName.trim(),
            category_parent_id: resolvedCategory.id,
          })
        ).unwrap()
      } else if (mode === 'addRoot') {
        response = await dispatch(
          createCategoryAsync({
            api_name: submittedName.trim(),
            category_parent_id: null,
          })
        ).unwrap()
      }
      if (response && !isApiPayloadSuccess(response)) {
        toast.error(
          getApiErrorMessage(
            response,
            mode === 'editName' ? 'Failed to update category' : 'Failed to create category'
          )
        )
        return
      }
      search(inputTerm)
      clearSelection()
      setActionInputName('')
      setSubcategoryInputName('')
      toast.success(mode === 'editName' ? 'Category updated' : 'Category created')
    } catch (error: any) {
      toast.error(getApiErrorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!pendingDeleteId) return
    setSubmitting(true)
    try {
      const response = await dispatch(deleteCategoryAsync({ id: pendingDeleteId })).unwrap()
      if (!isApiPayloadSuccess(response)) {
        toast.error(getApiErrorMessage(response, 'Failed to delete category'))
        return
      }
      clearSelection()
      search(inputTerm)
      toast.success('Category deleted')
    } catch (error: any) {
      toast.error(getApiErrorMessage(error))
    } finally {
      setSubmitting(false)
      setPendingDeleteId(null)
    }
  }

  const canMutateSelectedCategory = Boolean(resolvedCategory) && !resolvingCategory && !submitting
  const isNameUnchanged = actionInputName.trim() === resolvedCategory?.api_name?.trim()

  return (
    <div className='category-manage-page'>
      <main className='category-manage-shell'>
        <header className='category-manage-header'>
          <div>
            <button
              type='button'
              className='category-manage-back'
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1)
                } else {
                  navigate(`/listing-product-table/203/${platformId}`, { replace: true })
                }
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </button>
            <h1>Categories</h1>
            <p>
              Organise products into categories and subcategories. They show up in the Category
              field when you create or edit a product.
            </p>
          </div>
          <button
            type='button'
            className='category-manage-new-button'
            onClick={() => {
              clearSelection()
              setActionMode('addRoot')
              setActionInputName('')
            }}
            disabled={submitting}
          >
            <FontAwesomeIcon icon={faPlus} /> New category
          </button>
        </header>

        <section className='category-manage-layout' aria-label='Manage categories'>
          <div className='category-manage-tree-panel'>
            <label className='category-manage-search'>
              <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden='true' />
              <input
                type='search'
                placeholder='Search categories'
                value={inputTerm}
                onChange={handleInputChange}
                aria-label='Search categories'
              />
              {loading && <Spinner animation='border' size='sm' aria-label='Loading categories' />}
            </label>

            {actionMode === 'addRoot' && (
              <form
                className='category-manage-root-form'
                onSubmit={(event) => {
                  event.preventDefault()
                  void handleActionSubmit('addRoot', actionInputName)
                }}
              >
                <label htmlFor='category-root-name'>New category</label>
                <div className='category-manage-inline-form'>
                  <input
                    id='category-root-name'
                    type='text'
                    value={actionInputName}
                    onChange={(event) => setActionInputName(event.target.value)}
                    placeholder='Category name'
                    disabled={submitting}
                    autoFocus
                  />
                  <button type='submit' disabled={!actionInputName.trim() || submitting}>
                    {submitting ? <Spinner size='sm' animation='border' /> : 'Create'}
                  </button>
                  <button
                    type='button'
                    className='category-manage-cancel'
                    onClick={() => setActionMode('none')}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className='category-manage-tree-scroll' onScroll={handleScroll}>
              {treeData.length ? (
                <>
                  {renderTreeNodes(treeData, 0)}
                  {loading && hasMore && (
                    <div className='category-manage-loading-more'>Loading more categories...</div>
                  )}
                </>
              ) : (
                <div className='category-manage-tree-empty'>
                  {loading ? 'Loading categories...' : 'No categories found.'}
                </div>
              )}
            </div>
          </div>

          <div className='category-manage-details-panel'>
            {selectedNode ? (
              <>
                <nav className='category-manage-breadcrumbs' aria-label='Category path'>
                  {selectedPathLabels.map((label, index) => (
                    <React.Fragment key={`${label}-${index}`}>
                      {index > 0 && <span aria-hidden='true'>/</span>}
                      {index === selectedPathLabels.length - 1 ? (
                        <span aria-current='page'>{label}</span>
                      ) : (
                        <button
                          type='button'
                          onClick={() => handleSelectPath(selectedPathLabels.slice(0, index + 1))}
                        >
                          {label}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                </nav>

                <div className='category-manage-details-heading'>
                  <div>
                    <span className='category-manage-level'>Level {selectedPathLabels.length}</span>
                    <h2>{selectedNode.api_name}</h2>
                    {selectedPathLabels.length > 1 && (
                      <p className='category-manage-parent-label'>
                        Subcategory of {selectedPathLabels[selectedPathLabels.length - 2]}
                      </p>
                    )}
                  </div>
                  <div className='category-manage-detail-actions'>
                    <button
                      type='button'
                      className='category-manage-delete-button'
                      onClick={() => resolvedCategory && setPendingDeleteId(resolvedCategory.id)}
                      disabled={!canMutateSelectedCategory}
                    >
                      <FontAwesomeIcon icon={faTrashCan} /> Delete
                    </button>
                  </div>
                </div>

                {resolvingCategory && (
                  <div className='category-manage-status'>
                    <Spinner animation='border' size='sm' /> Resolving category...
                  </div>
                )}
                {!resolvingCategory && !resolvedCategory && (
                  <div className='category-manage-warning' role='status'>
                    This tree row could not be matched to a real category record. Select a more
                    specific result before editing, deleting, or adding a child.
                  </div>
                )}

                <form
                  className='category-manage-edit-form'
                  onSubmit={(event) => {
                    event.preventDefault()
                    void handleActionSubmit('editName', actionInputName)
                  }}
                >
                  <label htmlFor='category-edit-name'>Category name</label>
                  <div className='category-manage-inline-form'>
                    <input
                      id='category-edit-name'
                      type='text'
                      value={actionInputName}
                      onChange={(event) => setActionInputName(event.target.value)}
                      disabled={submitting || resolvingCategory || !resolvedCategory}
                    />
                    <button
                      type='submit'
                      disabled={!actionInputName.trim() || isNameUnchanged || !canMutateSelectedCategory}
                    >
                      {submitting ? <Spinner size='sm' animation='border' /> : 'Save name'}
                    </button>
                  </div>
                </form>

                <section className='category-manage-subcategories'>
                  <div className='category-manage-section-heading'>
                    <h3>Subcategories</h3>
                    <span>
                      {selectedNode.children?.length || 0}{' '}
                      {selectedNode.children?.length === 1 ? 'subcategory' : 'subcategories'}
                    </span>
                  </div>

                  <div
                    className={`category-manage-subcategory-container${selectedNode.children?.length ? '' : ' is-empty'}`}
                  >
                  {selectedNode.children?.length ? (
                    <div className='category-manage-subcategory-grid'>
                      {selectedNode.children.map((child) => (
                        <button
                          type='button'
                          className='category-manage-subcategory-card'
                          key={`${child.id}-${child.api_name}`}
                          onClick={() =>
                            handleSelectNode(child, [...selectedPathLabels, child.api_name])
                          }
                        >
                          <i className='bi bi-folder category-manage-folder-icon' aria-hidden='true' />
                          <span>{child.api_name}</span>
                          <FontAwesomeIcon icon={faChevronRight} className='category-manage-card-arrow' />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className='category-manage-no-subcategories'>No subcategories yet.</p>
                  )}
                  </div>

                  <form
                    className='category-manage-child-form'
                    onSubmit={(event) => {
                      event.preventDefault()
                      void handleActionSubmit('addChild', subcategoryInputName)
                    }}
                  >
                    <label htmlFor='category-subcategory-name'>Add a subcategory</label>
                    <div className='category-manage-inline-form'>
                      <input
                        id='category-subcategory-name'
                        type='text'
                        value={subcategoryInputName}
                        onChange={(event) => setSubcategoryInputName(event.target.value)}
                        placeholder={`e.g. a new type of ${selectedNode.api_name}`}
                        disabled={submitting || !canMutateSelectedCategory}
                      />
                      <button
                        type='submit'
                        disabled={!subcategoryInputName.trim() || !canMutateSelectedCategory}
                      >
                        {submitting ? <Spinner size='sm' animation='border' /> : <><FontAwesomeIcon icon={faPlus} /> Add</>}
                      </button>
                    </div>
                  </form>
                </section>
              </>
            ) : (
              <div className='category-manage-empty-state'>
                <div className='category-manage-empty-icon'>
                  <i className='bi bi-folder category-manage-folder-icon' aria-hidden='true' />
                </div>
                <h2>Select a category</h2>
                <p>Choose a category from the tree to view its details and subcategories.</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <CommonAlertModal
        isVisible={pendingDeleteId !== null}
        title='Delete Category'
        subtitle='Are you sure you want to delete this category? This action cannot be undone.'
        onSuccess={handleDelete}
        onCancel={() => setPendingDeleteId(null)}
        successBtnTitle='Delete'
        cancelBtnTitle='Cancel'
        successBtnClasses='btn btn-sm btn-danger m-2 py-2 px-4'
        cancelBtnClasses='btn btn-sm btn-outline btn-outline-danger btn-active-danger m-2 py-2 px-4'
        successDisabled={submitting}
        isTermsVisible={false}
        setIsVisible={(v) => !v && setPendingDeleteId(null)}
      />
    </div>
  )
}

export default CategoryManagePage
